import UserIcon from '@heroicons/react/24/outline/UserIcon.js';
import React from 'react';

import { tv } from '../../tv';
import { AiIcon } from '../ai-icon';
import { Icon } from '../icon';
import { autoColor } from '../tag/auto-color';
import { initialsOf } from './initials';

/**
 * Who or what something belongs to, at a glance: the photo when there is one,
 * the initials while it loads or when there is none, a glyph when the name
 * has no letters to give. The hue is the name's through Tag's `autoColor`;
 * the anatomy is Tag's soft chip with the initials on the measured
 * `-text-soft` step, because here the letters are the identity (avatar.md).
 */

const avatarStyles = tv({
  slots: {
    root: [
      'ck:relative ck:inline-flex ck:shrink-0 ck:items-center ck:justify-center',
      'ck:overflow-hidden ck:select-none ck:font-medium ck:uppercase ck:leading-none',
    ],
    /* Transparent until loaded, so the letters stand in and a slow photo never
     * flashes broken; the inner ring keeps a white photo's edge on white. It
     * carries the root's radius so the outline follows the circle. */
    image: 'ck:absolute ck:inset-0 ck:size-full ck:object-cover ck:inner-ring',
  },
  variants: {
    size: {
      sm: { root: 'ck:size-6 ck:text-xs' },
      md: { root: 'ck:size-8 ck:text-sm' },
      lg: { root: 'ck:size-10 ck:text-base' },
    },
    /* A single letter has room to sit one type step larger than a pair,
     * Radix Themes' rule; the pair keeps the size above. */
    letters: { one: {}, two: {} },
    shape: {
      circle: { root: 'ck:rounded-full', image: 'ck:rounded-full' },
      square: {},
    },
    /* The text step per hue; the wash is paired below: the bare alpha step
     * alone, and inside a group the same wash over the declared surface,
     * opaque so the neighbour under the overlap does not show through and
     * the same colour as the bare wash on that surface. */
    color: {
      red: { root: 'ck:text-red-text-soft' },
      orange: { root: 'ck:text-orange-text-soft' },
      yellow: { root: 'ck:text-yellow-text-soft' },
      green: { root: 'ck:text-green-text-soft' },
      teal: { root: 'ck:text-teal-text-soft' },
      cyan: { root: 'ck:text-cyan-text-soft' },
      blue: { root: 'ck:text-blue-text-soft' },
      indigo: { root: 'ck:text-indigo-text-soft' },
      purple: { root: 'ck:text-purple-text-soft' },
      pink: { root: 'ck:text-pink-text-soft' },
      brown: { root: 'ck:text-brown-text-soft' },
      neutral: { root: 'ck:text-text-muted' },
      ai: { root: 'ck:bg-fill ck:text-tone-contrast' },
    },
    grouped: {
      true: { root: 'ck:ring-2 ck:ring-surface-current' },
      false: {},
    },
    loaded: {
      true: {},
      false: { image: 'ck:opacity-0' },
    },
  },
  compoundVariants: [
    { color: 'red', grouped: false, class: { root: 'ck:bg-red-a-3' } },
    { color: 'red', grouped: true, class: { root: 'ck:wash-red' } },
    { color: 'orange', grouped: false, class: { root: 'ck:bg-orange-a-3' } },
    { color: 'orange', grouped: true, class: { root: 'ck:wash-orange' } },
    { color: 'yellow', grouped: false, class: { root: 'ck:bg-yellow-a-3' } },
    { color: 'yellow', grouped: true, class: { root: 'ck:wash-yellow' } },
    { color: 'green', grouped: false, class: { root: 'ck:bg-green-a-3' } },
    { color: 'green', grouped: true, class: { root: 'ck:wash-green' } },
    { color: 'teal', grouped: false, class: { root: 'ck:bg-teal-a-3' } },
    { color: 'teal', grouped: true, class: { root: 'ck:wash-teal' } },
    { color: 'cyan', grouped: false, class: { root: 'ck:bg-cyan-a-3' } },
    { color: 'cyan', grouped: true, class: { root: 'ck:wash-cyan' } },
    { color: 'blue', grouped: false, class: { root: 'ck:bg-blue-a-3' } },
    { color: 'blue', grouped: true, class: { root: 'ck:wash-blue' } },
    { color: 'indigo', grouped: false, class: { root: 'ck:bg-indigo-a-3' } },
    { color: 'indigo', grouped: true, class: { root: 'ck:wash-indigo' } },
    { color: 'purple', grouped: false, class: { root: 'ck:bg-purple-a-3' } },
    { color: 'purple', grouped: true, class: { root: 'ck:wash-purple' } },
    { color: 'pink', grouped: false, class: { root: 'ck:bg-pink-a-3' } },
    { color: 'pink', grouped: true, class: { root: 'ck:wash-pink' } },
    { color: 'brown', grouped: false, class: { root: 'ck:bg-brown-a-3' } },
    { color: 'brown', grouped: true, class: { root: 'ck:wash-brown' } },
    { color: 'neutral', grouped: false, class: { root: 'ck:bg-neutral-a-3' } },
    { color: 'neutral', grouped: true, class: { root: 'ck:wash-neutral' } },
    { size: 'sm', letters: 'one', class: { root: 'ck:text-sm' } },
    { size: 'md', letters: 'one', class: { root: 'ck:text-base' } },
    { size: 'lg', letters: 'one', class: { root: 'ck:text-lg' } },
    { size: 'sm', shape: 'square', class: { root: 'ck:rounded-2', image: 'ck:rounded-2' } },
    { size: 'md', shape: 'square', class: { root: 'ck:rounded-3', image: 'ck:rounded-3' } },
    { size: 'lg', shape: 'square', class: { root: 'ck:rounded-3', image: 'ck:rounded-3' } },
  ],
  defaultVariants: {
    size: 'md',
    letters: 'two',
    shape: 'circle',
    color: 'neutral',
    grouped: false,
    loaded: false,
  },
});

type AvatarSize = 'sm' | 'md' | 'lg';

const AvatarGroupContext = React.createContext<{ size: AvatarSize } | null>(null);

export interface AvatarProps {
  /** Whose avatar this is: the accessible name, the initials and the hue all come from it. */
  name: string;
  /** The photo, when there is one; the initials stand in until it loads, or if it fails. */
  src?: string;
  /** A glyph instead of the initials: a team, an organization, a notification. */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** 24, 32 or 40px; inside a group the group decides. */
  size?: AvatarSize;
  /** Circle for a person; square for an organization or a team. */
  shape?: 'circle' | 'square';
  /** The one tone: the agent, on the AI signature with its own glyph. */
  tone?: 'ai';
  /** The name is printed beside it, so the avatar itself says nothing. */
  isDecorative?: boolean;
  id?: string;
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { name, src, icon: Glyph, size: sizeProp, shape = 'circle', tone, isDecorative = false, id },
  ref,
) {
  const group = React.useContext(AvatarGroupContext);
  const size = group?.size ?? sizeProp ?? 'md';
  const initials = Glyph || tone === 'ai' ? '' : initialsOf(name);
  const [loadedSrc, setLoadedSrc] = React.useState<string>();
  const [failedSrc, setFailedSrc] = React.useState<string>();

  const { root, image } = avatarStyles({
    size,
    letters: initials.length === 1 ? 'one' : 'two',
    shape,
    color: tone ?? autoColor(name),
    grouped: Boolean(group),
    loaded: src !== undefined && loadedSrc === src,
  });

  return (
    <span
      ref={ref}
      id={id}
      className={root()}
      data-component="avatar"
      data-tone={tone}
      {...(isDecorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': name })}
    >
      {Glyph ? (
        <Icon as={Glyph} size={size} />
      ) : tone === 'ai' ? (
        <AiIcon size={size} fill="current" />
      ) : initials ? (
        <span aria-hidden="true">{initials}</span>
      ) : (
        <Icon as={UserIcon} size={size} />
      )}
      {src && failedSrc !== src ? (
        <img
          src={src}
          alt=""
          className={image()}
          onLoad={() => setLoadedSrc(src)}
          onError={() => setFailedSrc(src)}
        />
      ) : null}
    </span>
  );
});

const groupStyles = tv({
  slots: {
    /* Reversed, so the first member paints on top; the overlap is a quarter of the size. */
    root: 'ck:inline-flex ck:flex-row-reverse ck:items-center ck:justify-end',
  },
  variants: {
    size: {
      sm: { root: 'ck:[&>*+*]:-mr-1' },
      md: { root: 'ck:[&>*+*]:-mr-2' },
      lg: { root: 'ck:[&>*+*]:-mr-3' },
    },
  },
  defaultVariants: { size: 'md' },
});

export interface AvatarGroupProps {
  /** What the group is: "Assignees", "Approvers". */
  label: string;
  /** How many members show before the rest fold into a count. */
  max?: number;
  /** The one size every member wears. */
  size?: AvatarSize;
  children: React.ReactNode;
  id?: string;
}

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(function AvatarGroup(
  { label, max = 4, size = 'md', children, id },
  ref,
) {
  const members = React.Children.toArray(children);
  const shown = members.slice(0, Math.max(0, max));
  const surplus = members.length - shown.length;
  const { root } = groupStyles({ size });
  const { root: chip } = avatarStyles({ size, color: 'neutral', grouped: true, letters: 'two' });
  const context = React.useMemo(() => ({ size }), [size]);

  return (
    <div
      ref={ref}
      id={id}
      role="group"
      aria-label={label}
      className={root()}
      data-component="avatar-group"
    >
      <AvatarGroupContext.Provider value={context}>
        {surplus > 0 ? <span className={chip()}>+{surplus}</span> : null}
        {[...shown].reverse()}
      </AvatarGroupContext.Provider>
    </div>
  );
});
